import React, { useState, useEffect, useLayoutEffect, useCallback, lazy, Suspense } from 'react';
import { Navigate, useNavigate, useLocation, useBlocker, useParams, Outlet } from 'react-router-dom';
import posthog from 'posthog-js';
import { LogIn, UserPlus, LogOut, Loader, Settings, Mail, Crown, ArrowLeft, Copy, Check, X, Menu } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext';
import { CatalogModalProvider } from './contexts/CatalogModalContext';
import { supabase } from './supabaseClient';
import { validatePassword, getPasswordErrorMessage } from './utils';
import { PasswordRequirements } from './components/ui';
import { TOUR_STEPS, PAGE_PATHS } from './components/WelcomeWizard/tourSteps';
import ErrorBoundary from './components/ErrorBoundary';

// Lazy load - authenticated app components (user must log in first)
const Dashboard = lazy(() => import('./components/Dashboard'));
const SeedInventory = lazy(() => import('./components/SeedInventory'));
const PlantingSchedule = lazy(() => import('./components/PlantingSchedule'));
const HarvestLogger = lazy(() => import('./components/HarvestLogger'));
const GardenLayoutPlanner = lazy(() => import('./components/GardenLayoutPlanner'));
const Tasks = lazy(() => import('./components/Tasks'));
const NotificationSettings = lazy(() => import('./components/NotificationSettings'));
const UpgradeModal = lazy(() => import('./components/UpgradeModal'));
const NavigationTabs = lazy(() => import('./components/NavigationTabs'));
const WelcomeWizard = lazy(() => import('./components/WelcomeWizard'));
const TourEngine = lazy(() => import('./components/WelcomeWizard/TourEngine'));
const FinalCTAScreen = lazy(() => import('./components/WelcomeWizard/FinalCTAScreen'));

// Lazy load - secondary public pages
const EmailConfirm = lazy(() => import('./components/EmailConfirm'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const Pricing = lazy(() => import('./components/Pricing'));
const UserGuide = lazy(() => import('./components/UserGuide'));

// Loading fallback for lazy-loaded components
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="text-center">
      <Loader className="w-8 h-8 animate-spin text-[#52A675] mx-auto mb-4" />
      <p className="text-gray-500">Loading...</p>
    </div>
  </div>
);

// Redirect legacy /app/:path bookmarks to the correct new path
const LEGACY_PATHS = new Set(['seeds', 'plantings', 'harvests', 'tasks', 'layout', 'guide', 'login', 'signup']);
const LegacyAppRedirect = () => {
  const { path } = useParams();
  const target = LEGACY_PATHS.has(path) ? `/${path}` : '/';
  return <Navigate to={target} replace />;
};

// Optimized logo component
const Logo = ({ width = 150, className = '' }) => (
  <img
    src="/logo.svg"
    alt="Giddy Carrot"
    width={width}
    height={Math.round(width / 3)}
    style={{ width: `${width}px`, height: 'auto' }}
    className={className}
  />
);

const GiddyCarrotLogo = ({ size = 'large' }) => {
  const numWidth = size === 'large' ? 280 : size === 'medium' ? 200 : 140;
  return <Logo width={numWidth} className="mx-auto" />;
};

const Auth = ({ initialMode = 'login' }) => {
  const [isLogin, setIsLogin] = useState(initialMode === 'login');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [emailOptIn, setEmailOptIn] = useState(false);
  const [trialCode, setTrialCode] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { signIn, signUp, resetPassword, signInWithGoogle, loading: authLoading } = useAuth();

  // Reset loading state when auth state changes (e.g., after OAuth redirect or signout)
  useEffect(() => {
    if (!authLoading) {
      setIsLoading(false);
    }
  }, [authLoading]);

  const handleToggle = (mode) => {
    setIsLogin(mode === 'login');
    setShowForgotPassword(false);
    setError('');
    setSuccessMessage('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setFirstName('');
    setLastName('');
    setEmailOptIn(false);
    setTrialCode('');
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setError('');
    setSuccessMessage('');
    setIsLoading(true);

    try {
      await resetPassword(email);
      setSuccessMessage('Password reset link sent! Check your email to reset your password.');
      setEmail('');
      setShowForgotPassword(false);
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    if (!isLogin && (!firstName.trim() || !lastName.trim())) {
      setError('Please enter your first and last name');
      return;
    }

    // For signup, run full password validation; for login just require non-empty
    if (!isLogin) {
      const { valid } = validatePassword(password);
      if (!valid) {
        setError(getPasswordErrorMessage(password));
        return;
      }
    } else if (password.length < 1) {
      setError('Please enter your password');
      return;
    }

    if (!isLogin && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setError('');
    setSuccessMessage('');
    setIsLoading(true);

    try {
      if (isLogin) {
        await signIn(email, password);
      } else {
        await signUp(email, password, emailOptIn, firstName, lastName);
        // Show success message after signup
        const trialCodeMessage = trialCode.trim()
          ? ` Your trial code "${trialCode.trim().toUpperCase()}" will be applied after you confirm your email and sign in.`
          : '';
        setSuccessMessage(`Account created! Please check your email to confirm your account before signing in. If you don't see it, check your spam folder.${trialCodeMessage}`);
        // Store trial code in localStorage so we can auto-redeem after first login.
        // NOTE: This is intentionally stored in localStorage (not sessionStorage) so it
        // persists if the user verifies their email in the same browser but different tab.
        // Limitation: If the user completes email verification in a different browser/device,
        // the pending trial code will not be available for auto-redeem on first login.
        // They can still manually enter the code in Settings after logging in.
        if (trialCode.trim()) {
          localStorage.setItem('pending_trial_code', trialCode.trim().toUpperCase());
        }
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setFirstName('');
        setLastName('');
        setEmailOptIn(false);
        setTrialCode('');
        setIsLogin(true); // Switch to login tab
      }
    } catch (err) {
      const msg = err.message || 'An error occurred';
      const passwordError = getPasswordErrorMessage(password);
      if (!isLogin && msg.toLowerCase().includes('password') && passwordError) {
        // Signup password policy failure — show specific unmet requirements
        setError(passwordError);
      } else {
        // Login and all other backend failures — show the original message
        setError(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setSuccessMessage('');
    setIsLoading(true);

    try {
      await signInWithGoogle();
    } catch (err) {
      setError(err.message || 'Failed to sign in with Google');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-8">
          <GiddyCarrotLogo size="large" />
        </div>

        {/* Toggle Tabs */}
        <div className="flex mb-6 bg-gray-100 rounded-2xl p-1.5">
          <button
            onClick={() => handleToggle('login')}
            className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${isLogin
              ? 'bg-white text-[#52A675] shadow-md'
              : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            <LogIn size={18} />
            Sign In
          </button>
          <button
            onClick={() => handleToggle('signup')}
            className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${!isLogin
              ? 'bg-white text-[#52A675] shadow-md'
              : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            <UserPlus size={18} />
            Sign Up
          </button>
        </div>

        <div className={`bg-white border-2 rounded-3xl p-8 shadow-sm transition-all duration-300 ${isLogin ? 'border-gray-100' : 'border-[#C7F464]'
          }`}>
          {/* Header text */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-[#2F5233]">
              {showForgotPassword ? 'Reset your password' : isLogin ? 'Welcome back!' : 'Create your account'}
            </h2>
            <p className="text-gray-500 mt-1">
              {showForgotPassword
                ? 'Enter your email to receive a password reset link'
                : isLogin ? 'Sign in to continue to your garden' : 'Start tracking your garden today'}
            </p>
          </div>

          <div className="space-y-5">
            {/* Success message for signup */}
            {successMessage && (
              <div className="bg-green-50 border-2 border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm font-medium flex items-start gap-3">
                <Mail size={20} className="flex-shrink-0 mt-0.5" />
                <span>{successMessage}</span>
              </div>
            )}

            {/* Google Sign-In - hide during forgot password flow */}
            {!showForgotPassword && (
              <>
                <button
                  onClick={handleGoogleSignIn}
                  disabled={isLoading}
                  className="w-full py-3.5 px-4 bg-white border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold rounded-xl transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <Loader className="animate-spin" size={20} />
                  ) : (
                    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                  )}
                  {isLogin ? 'Sign in with Google' : 'Sign up with Google'}
                </button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500 font-medium">Or continue with email</span>
                  </div>
                </div>
              </>
            )}

            {/* First & Last Name - only for signup */}
            {!isLogin && !showForgotPassword && (
              <div className="animate-fadeIn grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-[#2F5233] mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Jane"
                    className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 transition-colors text-[#2F5233] placeholder-gray-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#2F5233] mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Doe"
                    className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 transition-colors text-[#2F5233] placeholder-gray-400"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-[#2F5233] mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="you@example.com"
                className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 transition-colors text-[#2F5233] placeholder-gray-400"
              />
            </div>

            {/* Password field - hide for forgot password */}
            {!showForgotPassword && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-[#2F5233]">
                    Password
                  </label>
                  {isLogin && (
                    <button
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-[#52A675] hover:text-[#4A9168] font-medium transition-colors"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="••••••••"
                  className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 transition-colors text-[#2F5233]"
                />
                {!isLogin && <PasswordRequirements password={password} />}
              </div>
            )}

            {/* Confirm Password - only for signup */}
            {!isLogin && !showForgotPassword && (
              <div className="animate-fadeIn">
                <label className="block text-sm font-semibold text-[#2F5233] mb-2">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="••••••••"
                  className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 transition-colors text-[#2F5233]"
                />
              </div>
            )}

            {/* Email Opt-in - only for signup */}
            {!isLogin && !showForgotPassword && (
              <div className="animate-fadeIn">
                <label className="flex items-start gap-3 cursor-pointer p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                  <div className="flex-shrink-0 mt-0.5">
                    <button
                      type="button"
                      onClick={() => setEmailOptIn(!emailOptIn)}
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${emailOptIn
                        ? 'bg-[#52A675] border-[#52A675]'
                        : 'bg-white border-gray-300 hover:border-[#52A675]'
                        }`}
                    >
                      {emailOptIn && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <span className="text-sm text-gray-600 leading-snug">
                    Send me occasional emails about new features, gardening tips, and updates. You can unsubscribe anytime.
                  </span>
                </label>
              </div>
            )}

            {/* Trial Code - only for signup */}
            {!isLogin && !showForgotPassword && (
              <div className="animate-fadeIn">
                <label className="block text-sm font-semibold text-[#2F5233] mb-2">
                  Trial Code <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={trialCode}
                  onChange={(e) => setTrialCode(e.target.value.toUpperCase())}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter code if you have one"
                  className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 transition-colors text-[#2F5233] uppercase"
                />
              </div>
            )}

            {error && (
              <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm font-medium">
                {error}
              </div>
            )}

            <button
              onClick={showForgotPassword ? handleForgotPassword : handleSubmit}
              disabled={isLoading}
              className={`w-full py-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${isLogin || showForgotPassword
                ? 'bg-[#52A675] hover:bg-[#4A9168] text-white'
                : 'bg-gradient-to-r from-[#52A675] to-[#C7F464] hover:from-[#4A9168] hover:to-[#b8e555] text-white'
                } disabled:bg-gray-300`}
            >
              {isLoading ? (
                <>
                  <Loader className="animate-spin" size={20} />
                  <span>Loading...</span>
                </>
              ) : (
                <>
                  {showForgotPassword ? (
                    <>
                      <Mail size={20} />
                      <span>Send Reset Link</span>
                    </>
                  ) : isLogin ? (
                    <>
                      <LogIn size={20} />
                      <span>Sign In</span>
                    </>
                  ) : (
                    <>
                      <UserPlus size={20} />
                      <span>Create Account</span>
                    </>
                  )}
                </>
              )}
            </button>

            {/* Back to Sign In link for forgot password */}
            {showForgotPassword && (
              <button
                onClick={() => {
                  setShowForgotPassword(false);
                  setError('');
                  setSuccessMessage('');
                }}
                className="w-full text-sm text-gray-600 hover:text-[#52A675] font-medium transition-colors"
              >
                ← Back to Sign In
              </button>
            )}
          </div>
        </div>

        {/* Bottom hint */}
        <p className="text-center text-sm text-gray-500 mt-6">
          {isLogin
            ? "New to Giddy Carrot? Click 'Sign Up' above to get started!"
            : "Already have an account? Click 'Sign In' above to continue."}
        </p>
      </div>
    </div>
  );
};

const AppShell = () => {
  const { user, signOut } = useAuth();
  const { isPro, refreshSubscription } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();

  const [showSettings, setShowSettings] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showMenuDropdown, setShowMenuDropdown] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [upgradePending, setUpgradePending] = useState(false);
  const [upgradePollingDone, setUpgradePollingDone] = useState(false);
  const [trialCodeRedeemed, setTrialCodeRedeemed] = useState(false);

  // Tour state (Stories 3–6)
  const [tourActive, setTourActive] = useState(false);
  const [tourStepIdx, setTourStepIdx] = useState(0);
  const [showFinalCTA, setShowFinalCTA] = useState(false);

  // Detect ?upgrade=success, clean URL, then poll for webhook confirmation
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgrade') === 'success') {
      window.history.replaceState({}, '', window.location.origin + window.location.pathname);
      setUpgradePending(true);

      let cancelled = false;
      const poll = async () => {
        for (let i = 0; i < 5; i++) {
          await new Promise((r) => setTimeout(r, 2000));
          if (cancelled) return;
          await refreshSubscription();
        }
        if (!cancelled) setUpgradePollingDone(true);
      };
      poll();
      return () => { cancelled = true; };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-dismiss banner once isPro is confirmed
  useEffect(() => {
    if (upgradePending && isPro) {
      posthog.capture('subscription_upgraded', { plan: 'yearly' });
      const t = setTimeout(() => {
        setUpgradePending(false);
        setUpgradePollingDone(false);
      }, 5000);
      return () => clearTimeout(t);
    }
  }, [upgradePending, isPro]);

  // Auto-dismiss "still processing" message after 10 seconds
  useEffect(() => {
    if (upgradePollingDone && !isPro) {
      const t = setTimeout(() => {
        setUpgradePending(false);
        setUpgradePollingDone(false);
      }, 10000);
      return () => clearTimeout(t);
    }
  }, [upgradePollingDone, isPro]);

  // Auto-redeem pending trial code from signup
  useEffect(() => {
    const redeemPendingTrialCode = async () => {
      // Check localStorage (preferred) and sessionStorage (backwards compatibility)
      const pendingCode = localStorage.getItem('pending_trial_code') ||
        sessionStorage.getItem('pending_trial_code');
      if (!pendingCode) return;

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        // Remove from storage only after we have a valid session
        // This prevents losing the code if auto-redeem fails due to no session
        localStorage.removeItem('pending_trial_code');
        sessionStorage.removeItem('pending_trial_code');

        const response = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/redeem-trial-code`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ code: pendingCode })
          }
        );

        if (response.ok) {
          setTrialCodeRedeemed(true);
          try {
            await refreshSubscription();
          } catch (refreshError) {
            console.error('Failed to refresh subscription after trial redemption, retrying once:', refreshError);
            try {
              await refreshSubscription();
            } catch (refreshRetryError) {
              console.error('Retrying subscription refresh after trial redemption also failed:', refreshRetryError);
            }
          }
          setTimeout(() => setTrialCodeRedeemed(false), 5000);
        }
      } catch (error) {
        console.error('Error auto-redeeming trial code:', error);
      }
    };

    redeemPendingTrialCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get the shareable signup link
  const getShareLink = () => `${window.location.origin}/?signup=true&utm_source=share&utm_medium=referral&utm_campaign=user_share&utm_content=user_${user?.id || 'unknown'}`;

  // Copy share link to clipboard
  const copyShareLink = () => {
    navigator.clipboard.writeText(getShareLink());
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  // Derive active tab from URL
  const getActiveTab = useCallback(() => {
    const path = location.pathname;
    if (path.startsWith('/seeds')) return 'seeds';
    if (path.startsWith('/plantings')) return 'plantings';
    if (path.startsWith('/harvests')) return 'harvests';
    if (path.startsWith('/tasks')) return 'tasks';
    if (path.startsWith('/layout')) return 'layout';
    if (path.startsWith('/guide')) return 'guide';
    return 'dashboard'; // default for / or /dashboard
  }, [location.pathname]);

  const activeTab = getActiveTab();

  // Scroll to top when tab changes
  // Using useLayoutEffect instead of useEffect ensures scroll happens synchronously
  // before browser paint, preventing visual flicker of content at old scroll position
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab]);

  // Block navigation when there are unsaved layout changes
  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) => {
        // Only block when leaving layout page with unsaved changes
        if (currentLocation.pathname === '/layout' &&
          nextLocation.pathname !== '/layout' &&
          window.checkLayoutUnsavedChanges) {
          return window.checkLayoutUnsavedChanges();
        }
        return false;
      },
      []
    )
  );

  // Handle blocker state
  useEffect(() => {
    if (blocker.state === 'blocked') {
      const confirmLeave = window.confirm('You have unsaved changes. Click OK to discard and continue, or Cancel to stay and save.');
      if (confirmLeave) {
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [blocker]);

  // Handle tab switch
  const handleTabSwitch = useCallback((tab) => {
    const tabToPath = {
      dashboard: '/',
      seeds: '/seeds',
      plantings: '/plantings',
      harvests: '/harvests',
      tasks: '/tasks',
      layout: '/layout',
      guide: '/guide'
    };

    navigate(tabToPath[tab] || '/');
  }, [navigate]);

  // Navigation handler for Dashboard clicks
  const handleNavigate = useCallback((tab) => {
    handleTabSwitch(tab);
  }, [handleTabSwitch]);

  // ─── Tour handlers (Stories 3–6 + Story 7 replay trigger) ──────────────────
  const handleShowTour = useCallback(() => {
    const firstRealIdx = TOUR_STEPS.findIndex((s) => !s.stub);
    if (firstRealIdx === -1) {
      console.error('[Tour] No non-stub tour steps found — aborting tour');
      return;
    }
    setTourStepIdx(firstRealIdx);
    setTourActive(true);
    navigate(PAGE_PATHS[TOUR_STEPS[firstRealIdx].page]);
  }, [navigate]);

  // Story 7: Guide "Replay the welcome tour" button dispatches this event
  useEffect(() => {
    const handler = () => handleShowTour();
    window.addEventListener('show-tour', handler);
    return () => window.removeEventListener('show-tour', handler);
  }, [handleShowTour]);

  const handleTourNext = useCallback(() => {
    let next = tourStepIdx + 1;
    while (next < TOUR_STEPS.length && TOUR_STEPS[next].stub) next++;
    if (next >= TOUR_STEPS.length) {
      setTourActive(false);
      setShowFinalCTA(true);
      return;
    }
    const path = PAGE_PATHS[TOUR_STEPS[next].page];
    if (!path) {
      console.error(`[Tour] Unknown page "${TOUR_STEPS[next].page}" for step "${TOUR_STEPS[next].id}"`);
      setTourActive(false);
      setShowFinalCTA(true);
      return;
    }
    setTourStepIdx(next);
    navigate(path);
  }, [tourStepIdx, navigate]);

  const handleTourSkip = useCallback(() => {
    setTourActive(false);
    setShowFinalCTA(true);
  }, []);

  const handleFinalCTADone = useCallback((path) => {
    setShowFinalCTA(false);
    navigate(path);
  }, [navigate]);

  // Handle app-level navigation from modals/components without direct access to handleTabSwitch
  useEffect(() => {
    const handleAppNavigate = (e) => {
      const tab = e.detail?.tab;
      if (tab) handleTabSwitch(tab);
    };
    window.addEventListener('app-navigate', handleAppNavigate);
    return () => window.removeEventListener('app-navigate', handleAppNavigate);
  }, [handleTabSwitch]);

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b-2 border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-1.5 flex items-center justify-between">
          <div className="flex items-center">
            <Logo width={130} />
          </div>
          <div className="flex items-center gap-2">
            {isPro ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-[#C7F464] to-[#52A675] text-[#2F5233] text-sm font-bold rounded-xl shadow-sm">
                <Crown size={16} />
                <span className="hidden sm:inline">Pro</span>
              </span>
            ) : (
              <button
                onClick={() => setShowUpgradeModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-[#F4A261] to-[#E8956B] text-white hover:from-[#E8956B] hover:to-[#F4A261] transition-all font-semibold text-sm shadow-sm hover:shadow-md"
                title="Upgrade to Pro"
              >
                <Crown size={16} />
                <span className="hidden sm:inline">Upgrade to Pro</span>
                <span className="sm:hidden">Upgrade</span>
              </button>
            )}
            <div className="relative">
              {showMenuDropdown && (
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowMenuDropdown(false)}
                />
              )}
              <button
                onClick={() => setShowMenuDropdown(!showMenuDropdown)}
                className="p-2 rounded-xl text-gray-500 hover:bg-gray-100 hover:text-[#52A675] transition-colors"
                aria-label="Menu"
              >
                <Menu size={22} />
              </button>
              {showMenuDropdown && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-white border-2 border-gray-100 rounded-xl shadow-xl z-50 py-1">
                  <button
                    onClick={() => { setShowSettings(true); setShowMenuDropdown(false); }}
                    className="w-full px-4 py-2.5 text-left text-sm text-[#2F5233] hover:bg-gray-50 flex items-center gap-3 transition-colors font-medium"
                  >
                    <Settings size={18} className="text-gray-400" />
                    Settings
                  </button>
                  <a
                    href="mailto:feedback@giddycarrot.com?subject=Giddy%20Carrot%20Feedback"
                    className="w-full px-4 py-2.5 text-left text-sm text-[#2F5233] hover:bg-gray-50 flex items-center gap-3 transition-colors font-medium"
                    onClick={() => setShowMenuDropdown(false)}
                  >
                    <Mail size={18} className="text-gray-400" />
                    Send Feedback
                  </a>
                  <button
                    onClick={() => { setShowInviteModal(true); setShowMenuDropdown(false); }}
                    className="w-full px-4 py-2.5 text-left text-sm text-[#2F5233] hover:bg-gray-50 flex items-center gap-3 transition-colors font-medium"
                  >
                    <UserPlus size={18} className="text-gray-400" />
                    Invite a Friend
                  </button>
                  <div className="border-t border-gray-100 my-1" />
                  <button
                    onClick={() => { signOut(); setShowMenuDropdown(false); }}
                    className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors font-medium"
                  >
                    <LogOut size={18} className="text-red-400" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Upgrade Success Banner */}
      {upgradePending && (
        <div className="bg-gradient-to-r from-[#C7F464] to-[#52A675] text-[#2F5233] px-6 py-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Crown size={24} className="text-[#2F5233]" />
              {isPro ? (
                <div>
                  <p className="font-bold">🎉 You're Pro! No limits on your garden.</p>
                  <p className="text-sm opacity-90">Enjoy unlimited tracking across all features.</p>
                </div>
              ) : upgradePollingDone ? (
                <div>
                  <p className="font-bold">Your payment is processing...</p>
                  <p className="text-sm opacity-90">It may take a moment to reflect. If this persists, email support@giddycarrot.com.</p>
                </div>
              ) : (
                <div>
                  <p className="font-bold">Finalizing your upgrade...</p>
                  <p className="text-sm opacity-90">Hang tight, this only takes a moment.</p>
                </div>
              )}
            </div>
            <button
              onClick={() => { setUpgradePending(false); setUpgradePollingDone(false); }}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Trial Code Redeemed Banner */}
      {trialCodeRedeemed && (
        <div className="bg-gradient-to-r from-[#C7F464] to-[#52A675] text-[#2F5233] px-6 py-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Crown size={24} className="text-[#2F5233]" />
              <div>
                <p className="font-bold">🎉 Trial Code Activated!</p>
                <p className="text-sm opacity-90">Welcome to Pro! Enjoy full access to all features during your trial.</p>
              </div>
            </div>
            <button
              onClick={() => setTrialCodeRedeemed(false)}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Notification Settings Modal */}
      <Suspense fallback={null}>
        <NotificationSettings isOpen={showSettings} onClose={() => setShowSettings(false)} onUpgrade={() => setShowUpgradeModal(true)} />
      </Suspense>

      {/* Upgrade Modal */}
      <Suspense fallback={null}>
        <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
      </Suspense>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-2xl sm:rounded-3xl max-w-md w-full max-h-[95vh] sm:max-h-[90vh] border-4 border-transparent relative overflow-hidden"
            style={{
              backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #C7F464, #52A675, #F4A261)',
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box'
            }}>
            <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(95vh-8px)] sm:max-h-[calc(90vh-8px)]">
              <h3 className="text-lg sm:text-xl font-bold text-[#2F5233] mb-2">Share Giddy Carrot</h3>
              <p className="text-gray-600 mb-4 sm:mb-6 text-sm">Invite friends and family to start tracking their garden with Giddy Carrot.</p>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-[#2F5233] mb-2">Share this link</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={getShareLink()}
                    readOnly
                    className="flex-1 px-3 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl text-base text-gray-600"
                  />
                  <button
                    type="button"
                    onClick={copyShareLink}
                    className="px-4 py-3 bg-[#52A675] hover:bg-[#4A9168] text-white rounded-xl transition-colors flex items-center gap-2"
                  >
                    {linkCopied ? <Check size={18} /> : <Copy size={18} />}
                    <span className="text-sm font-semibold hidden sm:inline">{linkCopied ? 'Copied!' : 'Copy'}</span>
                  </button>
                </div>
                {linkCopied && (
                  <p className="text-sm text-[#52A675] mt-2">Link copied to clipboard! 🎉</p>
                )}
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setShowInviteModal(false);
                    setLinkCopied(false);
                  }}
                  className="px-6 py-2.5 text-gray-600 hover:bg-gray-100 font-medium rounded-xl transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      <Suspense fallback={<div className="border-b-2 border-gray-100 bg-gray-50 h-14" />}>
        <NavigationTabs activeTab={activeTab} onTabSwitch={handleTabSwitch} />
      </Suspense>

      {/* Welcome Wizard — first-login onboarding modal (Stories 1 + 2) */}
      <Suspense fallback={null}>
        <WelcomeWizard onShowTour={handleShowTour} />
      </Suspense>

      {/* Tour engine — guided tooltip walkthrough (Stories 3–5) */}
      {tourActive && (
        <Suspense fallback={null}>
          <TourEngine
            stepIdx={tourStepIdx}
            onNext={handleTourNext}
            onSkip={handleTourSkip}
            activeTab={activeTab}
          />
        </Suspense>
      )}

      {/* Final CTA screen — shown after tour completes or is skipped (Story 6) */}
      {showFinalCTA && (
        <Suspense fallback={null}>
          <FinalCTAScreen
            onStartSeeds={() => handleFinalCTADone('/seeds')}
            onBuildLayout={() => handleFinalCTADone('/layout')}
            onExplore={() => handleFinalCTADone('/')}
          />
        </Suspense>
      )}

      <main>
        <div className={activeTab === 'dashboard' ? '' : 'hidden'}>
          <ErrorBoundary name="Dashboard">
            <Suspense fallback={<LoadingFallback />}>
              <Dashboard onNavigate={handleNavigate} onUpgrade={() => setShowUpgradeModal(true)} activeTab={activeTab} />
            </Suspense>
          </ErrorBoundary>
        </div>
        <div className={activeTab === 'seeds' ? '' : 'hidden'}>
          <ErrorBoundary name="Seeds & Plants">
            <Suspense fallback={<LoadingFallback />}>
              <SeedInventory onUpgrade={() => setShowUpgradeModal(true)} activeTab={activeTab} />
            </Suspense>
          </ErrorBoundary>
        </div>
        <div className={activeTab === 'layout' ? '' : 'hidden'}>
          <ErrorBoundary name="Layout Planner">
            <Suspense fallback={<LoadingFallback />}>
              <GardenLayoutPlanner onNavigate={handleNavigate} onUpgrade={() => setShowUpgradeModal(true)} activeTab={activeTab} />
            </Suspense>
          </ErrorBoundary>
        </div>
        <div className={activeTab === 'plantings' ? '' : 'hidden'}>
          <ErrorBoundary name="Planting Schedule">
            <Suspense fallback={<LoadingFallback />}>
              <PlantingSchedule onNavigate={handleNavigate} onUpgrade={() => setShowUpgradeModal(true)} activeTab={activeTab} />
            </Suspense>
          </ErrorBoundary>
        </div>
        <div className={activeTab === 'harvests' ? '' : 'hidden'}>
          <ErrorBoundary name="Harvest Logger">
            <Suspense fallback={<LoadingFallback />}>
              <HarvestLogger onNavigate={handleNavigate} onUpgrade={() => setShowUpgradeModal(true)} activeTab={activeTab} />
            </Suspense>
          </ErrorBoundary>
        </div>
        <div className={activeTab === 'tasks' ? '' : 'hidden'}>
          <ErrorBoundary name="Tasks">
            <Suspense fallback={<LoadingFallback />}>
              <Tasks onUpgrade={() => setShowUpgradeModal(true)} activeTab={activeTab} />
            </Suspense>
          </ErrorBoundary>
        </div>
        <div className={activeTab === 'guide' ? '' : 'hidden'}>
          <ErrorBoundary name="User Guide">
            <Suspense fallback={<LoadingFallback />}>
              <UserGuide />
            </Suspense>
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
};

// Landing page URL - external link to main site
const LANDING_URL = 'https://giddycarrot.com';

// Auth page wrapper with back button
const AuthPage = ({ mode }) => {
  return (
    <div>
      <div className="absolute top-4 left-4 z-10">
        <a
          href={LANDING_URL}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-[#2F5233] hover:bg-gray-100 rounded-xl transition-colors font-medium"
        >
          <ArrowLeft size={18} />
          Back to Home
        </a>
      </div>
      <Auth initialMode={mode} />
    </div>
  );
};

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader className="animate-spin text-[#52A675]" size={48} />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Public route wrapper (redirects to app if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader className="animate-spin text-[#52A675]" size={48} />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Root layout that handles password reset
const RootLayout = () => {
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [isConfirmingEmail, setIsConfirmingEmail] = useState(false);
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Track page views for SPA navigation
  useEffect(() => {
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      path: location.pathname
    });
  }, [location.pathname]);

  useEffect(() => {
    // Check if this is a password reset or email confirmation callback
    // Supabase sends tokens in the hash like #access_token=...&type=recovery or type=signup
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const type = hashParams.get('type');

    // Also check if we're on the reset-password path
    const isResetPasswordPath = window.location.pathname === '/reset-password';

    if (type === 'recovery' || isResetPasswordPath) {
      setIsResettingPassword(true);
    } else if (type === 'signup') {
      // Email confirmation - show loading while Supabase processes the token
      setIsConfirmingEmail(true);
    }
  }, []);

  // Handle email confirmation redirect after auth state updates
  useEffect(() => {
    if (isConfirmingEmail && !authLoading && user) {
      // User is now authenticated, redirect to app
      navigate('/', { replace: true });
    }
  }, [isConfirmingEmail, authLoading, user, navigate]);

  // Show reset password page if it's a recovery link
  if (isResettingPassword) {
    return <ResetPassword />;
  }

  // Show loading while confirming email
  if (isConfirmingEmail && (authLoading || !user)) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <Loader className="animate-spin text-[#52A675] mb-4" size={48} />
        <p className="text-gray-600">Confirming your email...</p>
      </div>
    );
  }

  return <Outlet />;
};

// App wrapper with providers
const AppWrapper = ({ children }) => {
  return (
    <AuthProvider>
      <SubscriptionProvider>
        <CatalogModalProvider>
          {children}
        </CatalogModalProvider>
      </SubscriptionProvider>
    </AuthProvider>
  );
};

// Route configuration for createBrowserRouter
export const routeConfig = [
  {
    path: '/',
    element: <AppWrapper><RootLayout /></AppWrapper>,
    children: [
      {
        index: true,
        element: <ProtectedRoute><AppShell /></ProtectedRoute>
      },
      {
        path: 'login',
        element: <PublicRoute><AuthPage mode="login" /></PublicRoute>
      },
      {
        path: 'signup',
        element: <PublicRoute><AuthPage mode="signup" /></PublicRoute>
      },
      {
        path: 'terms',
        element: <Suspense fallback={<LoadingFallback />}><TermsOfService /></Suspense>
      },
      {
        path: 'privacy',
        element: <Suspense fallback={<LoadingFallback />}><PrivacyPolicy /></Suspense>
      },
      {
        path: 'pricing',
        element: <Suspense fallback={<LoadingFallback />}><Pricing /></Suspense>
      },
      {
        path: 'seeds',
        element: <ProtectedRoute><AppShell /></ProtectedRoute>
      },
      {
        path: 'plantings',
        element: <ProtectedRoute><AppShell /></ProtectedRoute>
      },
      {
        path: 'harvests',
        element: <ProtectedRoute><AppShell /></ProtectedRoute>
      },
      {
        path: 'tasks',
        element: <ProtectedRoute><AppShell /></ProtectedRoute>
      },
      {
        path: 'layout',
        element: <ProtectedRoute><AppShell /></ProtectedRoute>
      },
      {
        path: 'guide',
        element: <ProtectedRoute><AppShell /></ProtectedRoute>
      },
      {
        path: 'auth/confirm',
        element: <Suspense fallback={<LoadingFallback />}><EmailConfirm /></Suspense>
      },
      // Redirects for old /app/* bookmarks
      {
        path: 'app',
        element: <Navigate to="/" replace />
      },
      {
        path: 'app/:path',
        element: <LegacyAppRedirect />
      },
      {
        path: 'reset-password',
        element: <Suspense fallback={<LoadingFallback />}><ResetPassword /></Suspense>
      },
      {
        path: '*',
        element: <Navigate to="/" replace />
      }
    ]
  }
];

export default function App() {
  return (
    <AuthProvider>
      <SubscriptionProvider>
        <RootLayout />
      </SubscriptionProvider>
    </AuthProvider>
  );
}
